// Address Augmentation
//
// Due to limitations with the Shopify Carrier Service (namely, particular
// address fields like `addressType` or `address3` aren't settable from
// the front end in a way that's passed through to the Carrier Service API
// endpoint), we need to find another way to send additional information to
// the endpoint.
//
// We've chosen to do this by augmenting the `address2` field with a JSON
// object containing additional values, like this:
//
//   'address2': '101 Main Street|||{"addressType":"Residential"}'
//
// To help manage this augmented information, we provide the two functions
// below to help parse the augmented information from a serialized string
// version, and also to update an `address2` string with new address or
// augmented information.
//
// These methods are used in the following places:
//
// * When selecting an address from the autocompleted on the customer
//   information page in checkout (see qas-addresses.js);
//
// * When selecting a custom click & collect or parcel locker address
//   in the delivery method step in checkout (@TODO).

const replaceQuotes = (string) => {
  let replacedString = string;
  if (string) {
    replacedString = string.replace(/&(l|g|quo)t;/g, (a, b) => {
      return {
        l: '<',
        g: '>',
        quo: '"',
      }[b];
    });
  }
  return replacedString;
};

/**
 * Parse an `address2` value in a raw string format, and return the component
 * parts, eg:
 *
 * parseAugmentedAddress2('101 Main Street|||{"addressType":"Residential"}');
 *
 * > {
 * >   "address": "101 Main Street",
 * >   "data": {
 * >     "addressType": "Residential"
 * >   }
 * > }
 *
 * @param address2          A raw `address2` value.
 *
 * @returns {{address: (*|string), data: any}}
 */
export const parseAugmentedAddress2 = (address2) => {
  const parts = address2.split('|||');
  const address = parts[0];
  const decoded = replaceQuotes(parts[1]);
  const data = JSON.parse(decoded || '{}');

  return {
    address,
    data,
  };
};

export const parseAugmentedAddress3 = (address2) => {
  if (address2) {
    const parts = address2.split('|||');
    const address = parts[0];
    const ids = parts[1] ? parts[1].split('|') : ['', ''];

    return {
      address,
      mappingId: ids[0] ? ids[0] : '',
      optionId: ids[1] ? ids[1] : '',
    };
  }

  return {
    address: '',
    mappingId: '',
    optionId: '',
  };
};

export const parseAugmentedAddress4 = (address2) => {
  if (address2) {
    const parts = address2.split(/(\.+)$/);
    const address = parts[0];
    const dots = parts[1] ? parts[1] : '';

    return {
      address,
      dots,
    };
  }

  return {
    address: '',
    dots: '',
  };
};

export const getCleanAddress = (shippingAddress) => {
  if (!shippingAddress) {
    return null;
  }
  const cleanAddress2 = shippingAddress.address2 === '' ? {address: ''} : parseAugmentedAddress4(shippingAddress.address2);
  const address2 = cleanAddress2.address === '' ? '' : `${cleanAddress2.address}, `;
  const cleanAddress = `${shippingAddress.address1}, ${address2} ${shippingAddress.city} ${shippingAddress.province_code}, ${shippingAddress.country}`;
  return cleanAddress;
};


export const convertFormattedAddress = (elem) => {
  $(elem).each((index, address) => {
    const fullAddress = $(address).html().split('<br>');
    $(address).html('');
    fullAddress.map((line) => {
      if (line.indexOf('|||') > -1) {
        const parts = parseAugmentedAddress2(line);
        if (parts.address !== '') {
          $(address).append(`${parts.address}<br>`);
        }
      } else {
        $(address).append(`${line}<br>`);
      }
      return line;
    });
  });
};

export const convertSingleLineAddress = (elem) => {
  $(elem).each((index, address) => {
    const newLine = $(address).text().replace(/\|\|\|\{.*?\},\s+/, '');
    $(address).text(newLine);
  });
};

/**
 * Given an `address2` value in a raw string format, parse it and then update
 * the address part and the augmented data with the provided values, eg:
 *
 * updateAugmentedAddress2(
 *   '101 Main Street|||{"addressType":"3"}',
 *   '48 River Road',
 *   {
 *     "cc_postcode": "3052"
 *   }
 * );
 *
 * > '48 River Road|||{"addressType":"3","cc_postcode":"3052"}'
 *
 * @param currentAddress2   The current raw string value of address2.
 * @param newAddress        The new value to set for the customer-facing address2 value.
 *                          If left undefined, the current value will be retained.
 * @param newData           Any new data values to set in the augmented part of the data.
 *
 * @returns {string}
 */
export const updateAugmentedAddress2 = (currentAddress2, newAddress, newData = {}) => {
  const parsedCurrentAddress2 = parseAugmentedAddress2(currentAddress2);

  return [
    newAddress === null ? parsedCurrentAddress2.address : newAddress,
    JSON.stringify(Object.assign({}, parsedCurrentAddress2.data, newData)),
  ].join('|||');
};

export const updateAugmentedAddress3 = (currentAddress2, newAddress) => {
  if ( currentAddress2 ) {
    const parsedCurrentAddress2 = parseAugmentedAddress3(currentAddress2);

    return [
      newAddress === null ? parsedCurrentAddress2.address : newAddress,
      `${parsedCurrentAddress2.mappingId}|${parsedCurrentAddress2.optionId}`,
    ].join('|||');
  }

  return '||||'
};

/**
 * Given a new `address2` value, this method makes two Ajax requests - one to fetch
 * the contact information step of the checkout and a second to then submit the form
 * on that page with an updated `address2` value. This method provides an optional
 * `callbacks` argument, which expects a hash of success, error, and completion
 * callback functions.
 *
 * @param newAddress2
 * @param callbacks
 */
export const setAddress2 = (newAddress2, callbacks = {}) => {

  $.ajax({
    method: 'GET',
    url: `https://${window.location.host}${window.location.pathname}?step=contact_information`,
    success: (response) => {
      const $contactForm = $(response).find('[data-customer-information-form="true"]');
      $contactForm.append(`<input type="hidden" name="checkout[shipping_address][address2]" value='${newAddress2}' />`);
      $.ajax({
        method: 'POST',
        url: $contactForm.attr('action'),
        data: $contactForm.serialize(),
        success: (response) => {
          callbacks.success && callbacks.success(response);
        },
        error: (response) => {
          callbacks.error && callbacks.error(response);
        },
        complete: () => {
          callbacks.complete && callbacks.complete();
        },
      });
    },
    error: () => {
      callbacks.error && callbacks.error(response);
      callbacks.complete && callbacks.complete();
    },
  });

};
